// Редовете на прехвърлянето като карти (под десктоп ширина): име · наличност ·
// ✕, после полетата Количество и Партида с етикет и редове „Остава · Става" и
// „Ед. цена · Нето · Бруто". Полетата носят СЪЩИТЕ id-та (`trx-{продукт}-{кол}`)
// като клетките на решетката, затова фокусът след добавяне и стрелките работят
// еднакво.
import { Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatMoney, formatUnitPrice } from "../../components/numbers";
import { formatQty } from "../../components/qty";
import { PickCell } from "../../components/PickCell";
import type { TransferTableProps } from "./TransferWorksheetTable";
import { FLAT_CELL_ERROR_CLASS, cellId, lotLabel } from "./transferWorksheetShared";

// Полетата на картата — с рамка (докосване с пръст), 16px срещу зуума на iOS
const FIELD_CLASS =
  "box-border w-full rounded-lg border border-shade-3 bg-surface px-2.5 py-2.5 text-base leading-[normal] text-ink outline-none focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-tint)]";

export const TransferLineCards = ({
  rows,
  fromWarehouseName,
  toWarehouseName,
  onQuantityChange,
  onQuantityBlur,
  onLotChange,
  onRemove,
  onSideExit,
}: TransferTableProps) => {
  const { t } = useTranslation();
  const label = (text: string) => (
    <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">{text}</span>
  );

  return (
    <div>
      {rows.map((row, index) => {
        const unitSuffix = row.unit ? ` ${row.unit}` : "";
        return (
          <div
            key={row.productId}
            className={`flex flex-col gap-2.5 border-t border-shade-1 px-3.5 py-3 ${
              row.overStock ? "bg-danger-tint/40" : ""
            }`}
          >
            <div className="flex items-start gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-semibold text-ink">
                  {index + 1}. {row.product?.name ?? `#${row.productId}`}
                </span>
                <span className={`text-xs ${row.overStock ? "font-semibold text-danger-ink" : "text-muted"}`}>
                  {t("inventoryPage.colAvailable")}:{" "}
                  {row.available == null ? "…" : `${formatQty(row.available, row.decimals)}${unitSuffix}`}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRemove(row.productId)}
                aria-label={t("inventoryPage.wsClearRowHint")}
                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-[7px] border border-shade-3 bg-surface text-body"
              >
                <CloseOutlined />
              </button>
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
              <label className="flex flex-col gap-1">
                {label(`${t("inventoryPage.colQty")}${row.unit ? ` · ${row.unit}` : ""}`)}
                <input
                  id={cellId(row.productId, 0)}
                  data-rk={row.productId}
                  data-c={0}
                  inputMode="decimal"
                  enterKeyHint="next"
                  value={row.draft.quantity}
                  onChange={(event) => onQuantityChange(row.productId, event.target.value)}
                  onBlur={() => onQuantityBlur(row.productId)}
                  onFocus={(event) => event.currentTarget.select()}
                  placeholder={row.decimals > 0 ? `0,${"0".repeat(row.decimals)}` : "0"}
                  className={`${FIELD_CLASS} text-right font-sheet font-semibold ${
                    row.overStock ? FLAT_CELL_ERROR_CLASS : ""
                  }`}
                />
              </label>
              <label className="flex min-w-0 flex-col gap-1">
                {label(t("inventoryPage.colBatch"))}
                <PickCell
                  id={cellId(row.productId, 1)}
                  rowKey={row.productId}
                  col={1}
                  className={`${FIELD_CLASS} cursor-pointer truncate text-left`}
                  onSideExit={(step) => onSideExit(row.productId, 1, step)}
                  renderEditor={(close) => (
                    <Select
                      autoFocus
                      defaultOpen
                      value={row.draft.lotId ? String(row.draft.lotId) : "fifo"}
                      onChange={(value) => {
                        onLotChange(row.productId, value === "fifo" ? null : Number(value));
                        close();
                      }}
                      onBlur={() => close(false)}
                      popupMatchSelectWidth={false}
                      className="w-full"
                      options={[
                        { value: "fifo", label: t("inventoryPage.fifo") },
                        ...row.lots.map((lot) => ({
                          value: String(lot.id),
                          label: lotLabel(lot, row.decimals, row.unit, t),
                        })),
                      ]}
                    />
                  )}
                >
                  {row.draft.lotId
                    ? (row.lots
                        .filter((lot) => lot.id === row.draft.lotId)
                        .map((lot) => lotLabel(lot, row.decimals, row.unit, t))[0] ??
                      t("inventoryPage.fifo"))
                    : t("inventoryPage.fifo")}
                </PickCell>
              </label>
            </div>
            <div className="flex flex-wrap justify-end gap-x-2.5 gap-y-1 text-[12.5px] text-body">
              <span>
                {t("inventoryPage.trxRemains", { warehouse: fromWarehouseName ?? "" })}{" "}
                <span className="font-sheet text-ink">
                  {row.remainingInSource == null
                    ? "…"
                    : `${formatQty(Math.max(0, row.remainingInSource), row.decimals)}${unitSuffix}`}
                </span>
              </span>
              <span>
                · {t("inventoryPage.trxBecomes", { warehouse: toWarehouseName ?? "" })}{" "}
                <span className="font-sheet text-ok-deep">
                  {row.resultingInTarget == null
                    ? "…"
                    : `${formatQty(row.resultingInTarget, row.decimals)}${unitSuffix}`}
                </span>
              </span>
            </div>
            <div className="flex flex-wrap justify-end gap-x-2.5 gap-y-1 text-[12.5px] text-body">
              <span>
                {t("inventoryPage.colUnitPrice")}{" "}
                <span className="font-sheet text-ink">
                  {row.unitPrice != null ? formatUnitPrice(row.unitPrice) : "—"}
                </span>
              </span>
              <span>
                · {t("inventoryPage.colNet")}{" "}
                <span className="font-sheet text-ink">{row.net > 0 ? formatMoney(row.net) : "—"}</span>
              </span>
              <span>
                · {t("inventoryPage.colGross")}{" "}
                <span className="font-sheet font-semibold text-ink">
                  {row.gross > 0 ? formatMoney(row.gross) : "—"}
                </span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TransferLineCards;
